import { store } from '@/store';
import * as PleasedApi from '@/common/api/api.pleased';
import { analytics, CRITICAL_EVENTS } from '@/common/logs';
import { parseErrorMessage } from '@/common/error';
import { reportingService } from '@/common/reporting';
import { resolveGuestDisplayName } from '@/common/guestForm';
import { getFallbackUserName } from '@/common/userDisplayName';
import { useBotUser } from '@/stores/useBotUser';
import type { GuestTicketForm } from '@/components/OpenRequestModal/openRequestForm.types';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

interface CreateGuestOpenRequestOptions {
  subject: string;
  /** Final description to submit (caller resolves any optional-field fallback). */
  description: string;
}

/**
 * Side-effectful "create a guest open request directly" action for the
 * OTP-disabled open-request flow. Mirrors the guest branch of
 * `OpenRequestModal.vue` (`onProceedClick` → `openRequest`): anonymous token →
 * commit guest user to the store → `createTicket`.
 *
 * Owns no component/UI state: it takes the reactive `form` and a subject, and
 * returns the created ticket id as a string. The caller keeps ownership of
 * loading/step/error state and the try/catch around this call.
 */
export const useGuestFormOpenRequest = () => {
  const botUserStore = useBotUser();

  const createGuestOpenRequest = async (
    form: GuestTicketForm,
    { subject, description }: CreateGuestOpenRequestOptions
  ): Promise<Awaited<ReturnType<typeof PleasedApi.createTicket>>> => {
    try {
      // Reuse the guest's existing token if they already have one; only mint a new anonymous token otherwise.
      const savedGuest = botUserStore.guestUser;
      const userData = savedGuest?.token ? savedGuest : await PleasedApi.getAnonymousToken();
      const displayName = resolveGuestDisplayName(form, getFallbackUserName());
      const guestConfig = {
        ...userData,
        isGuest: true,
        name: form.name.trim() || userData.name || displayName,
        email: form.email.trim() || userData.email || '',
        phone: form.phone.trim() || userData.phone || '',
      };
      const visitorInfo = {
        displayName,
        email: form.email.trim(),
        name: displayName,
        phone: form.phone.trim(),
        isGuest: true,
      };
      store.commit('user/setGuestFormData', visitorInfo);
      store.commit('user/updateGuestUser', guestConfig);
      store.commit('user/updatePleasedUser', guestConfig);
      botUserStore.setGuestUser(guestConfig);

      reportingService.openRequestClicked();

      return await PleasedApi.createTicket({
        subject,
        description,
        // Falls back to a previously saved email so we don't send an empty recipient.
        recipientAddress: guestConfig.email,
        ticketFields: [],
        userLogs: reportingService.getLogs(),
        chatbotSessionId: analytics.sessionId,
      });
    } catch (err: unknown) {
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_INITIATED_ERROR,
        metadata: {
          error: parseErrorMessage(err),
        },
      });
      captureEventException(err, ErrorTrackingEvent.GuestForm);
      throw err;
    }
  };

  return { createGuestOpenRequest };
};
